import React, { useState, useEffect } from 'react';
import { useAuth, useAuthDialog } from '../App';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Address helpers (user-specific localStorage, stores array of last 5) ─────
const addrKey = (userId) => `biomall_rfq_addr_${userId}`;

export function emptyAddr() {
  return { org_name: '', address_line1: '', city: '', state: '', postal_code: '', country: '', gst: '' };
}

// Only consider an address complete when all required fields are filled (country is mandatory)
function isAddrComplete(addr) {
  return ['address_line1', 'city', 'state', 'postal_code', 'country'].every(f => (addr?.[f] || '').trim());
}

function loadAddresses(userId) {
  try {
    const raw = localStorage.getItem(addrKey(userId));
    const parsed = raw ? JSON.parse(raw) : null;
    // Support both old single-object format and new array format
    if (Array.isArray(parsed)) return parsed;
    if (parsed && typeof parsed === 'object') return [parsed];
    return [];
  } catch { return []; }
}

function saveAddress(userId, addr) {
  // Only save when ALL required fields are filled — prevents incomplete chips
  if (!isAddrComplete(addr)) return;
  const list = loadAddresses(userId);
  const dupIdx = list.findIndex(a => a.address_line1 === addr.address_line1 && a.city === addr.city);
  let newList;
  if (dupIdx >= 0) {
    // Update the existing entry with the latest data (preserves country, GST, etc.)
    newList = [...list];
    newList[dupIdx] = { ...newList[dupIdx], ...addr };
  } else {
    newList = [addr, ...list].slice(0, 5);
  }
  localStorage.setItem(addrKey(userId), JSON.stringify(newList));
}

// ── Phone number persistence (per-user localStorage) ──────────────────────────
const phoneKey = (userId) => `biomall_rfq_phone_${userId}`;
function loadPhone(userId) { return localStorage.getItem(phoneKey(userId)) || ''; }
function savePhone(userId, phone) { if (phone) localStorage.setItem(phoneKey(userId), phone); }

// Keep exports for backward compat
export const ADDR_KEY = 'biomall_rfq_address';
export function loadSavedAddr() { return emptyAddr(); }

// ─── Address sub-form (top-level to prevent remount on every keystroke) ───────
export function AddrForm({ val, set, title, showGst = true }) {
  const fields = [
    { k: 'org_name',      ph: 'Organisation name', full: true },
    { k: 'address_line1', ph: 'Street address *',  full: true },
    { k: 'city',          ph: 'City *' },
    { k: 'state',         ph: 'State *' },
    { k: 'postal_code',   ph: 'Postal code *' },
    { k: 'country',       ph: 'Country *' },
  ];
  if (showGst) fields.push({ k: 'gst', ph: 'GST / Tax number' });

  return (
    <div className="border border-gray-200 rounded-xl p-3 mb-3">
      <div className="text-xs font-bold text-gray-700 mb-2">{title}</div>
      <div className="grid grid-cols-2 gap-2">
        {fields.map(({ k, ph, full }) => (
          <input key={k}
            value={val[k] || ''} onChange={e => set(k, e.target.value)}
            placeholder={ph}
            className={`px-3 py-1.5 text-xs border border-gray-200 rounded-lg outline-none focus:border-blue-400 ${full ? 'col-span-2' : ''}`}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Saved address picker chips ───────────────────────────────────────────────
function SavedAddressPicker({ addresses, onSelect }) {
  if (!addresses.length) return null;
  return (
    <div className="mb-2">
      <div className="text-xs text-gray-400 mb-1.5">Saved addresses — click to fill:</div>
      <div className="flex flex-wrap gap-1.5">
        {addresses.map((addr, i) => (
          <button key={i} type="button" onClick={() => onSelect(addr)}
            className="text-xs px-2.5 py-1 border border-gray-200 rounded-full hover:border-blue-400 hover:bg-blue-50 text-gray-600 transition-colors truncate max-w-xs">
            {[addr.org_name, addr.address_line1, addr.city, addr.state].filter(Boolean).join(', ')}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Multi-product RFQ modal ──────────────────────────────────────────────────
export function QuoteRFQModal({ brand, items, onRemoveItem, onClose, onSubmitDone }) {
  return <QuoteRFQForm brand={brand} items={items} onRemoveItem={onRemoveItem} onClose={onClose} onSubmitDone={onSubmitDone} />;
}

// ── Backend address helpers ────────────────────────────────────────────────────
async function fetchAddressesFromDB(token) {
  try {
    const res = await fetch(`${API}/api/buyer/addresses`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    return await res.json();            // { addresses: [...], phone: "..." }
  } catch { return null; }
}

async function saveAddressesToDB(token, billingAddress, shippingAddress, phone) {
  try {
    await fetch(`${API}/api/buyer/addresses`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ billing_address: billingAddress, shipping_address: shippingAddress || null, phone }),
    });
  } catch { /* non-blocking */ }
}

// Inner form — progressive auth: fill first, login gate only at submit
function QuoteRFQForm({ brand, items, onRemoveItem, onClose, onSubmitDone }) {
  const { user }        = useAuth();
  const { openLabAuth } = useAuthDialog();
  const userId   = user ? (user.user_id || user.id || user.email || 'guest') : 'guest';
  const token    = localStorage.getItem('lab_token') || sessionStorage.getItem('lab_session_token') || '';
  const localSaved = loadAddresses(userId);

  const [contact, setContact] = useState({
    name:         user?.name  || '',
    email:        user?.email || '',
    organization: user?.organization || user?.company || '',
    phone:        user?.phone || loadPhone(userId),
  });
  const [billing, setBilling]       = useState({ ...(localSaved[0] || emptyAddr()), org_name: (localSaved[0]?.org_name) || user?.organization || user?.company || '' });
  const [sameAsShipping, setSame]   = useState(true);
  const [shipping, setShipping]     = useState({ ...emptyAddr(), org_name: user?.organization || user?.company || '' });
  const [notes, setNotes]           = useState('');
  const [qtys, setQtys]             = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState('');
  const [done, setDone]             = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState(localSaved);

  // On mount: fetch saved addresses from DB (overrides localStorage)
  useEffect(() => {
    if (!token) return;
    fetchAddressesFromDB(token).then(data => {
      if (!data) return;
      const billingDb  = data.billing_address  || {};
      const shippingDb = data.shipping_address || {};
      const dbAddrs    = data.addresses || [];
      const dbPhone    = data.phone || '';

      // Pre-fill billing
      const hasBilling = billingDb.address_line1;
      if (hasBilling) {
        setBilling(prev => ({ ...billingDb, org_name: billingDb.org_name || prev.org_name || user?.organization || user?.company || '' }));
      } else if (dbAddrs.length > 0) {
        setBilling(prev => ({ ...dbAddrs[0], org_name: dbAddrs[0].org_name || prev.org_name || user?.organization || user?.company || '' }));
      }

      // Pre-fill shipping if different from billing
      if (shippingDb.address_line1 && shippingDb.address_line1 !== (billingDb.address_line1 || '')) {
        setShipping(shippingDb);
        setSame(false);
      }

      if (dbAddrs.length > 0) {
        setSavedAddresses(dbAddrs);
        localStorage.setItem(addrKey(userId), JSON.stringify(dbAddrs));
      }
      if (dbPhone) {
        setContact(p => ({ ...p, phone: p.phone || dbPhone }));
        savePhone(userId, dbPhone);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-populate contact + billing org_name when user logs in
  useEffect(() => {
    if (!user) return;
    setContact(prev => ({
      name:         prev.name         || user.name         || '',
      email:        prev.email        || user.email        || '',
      organization: prev.organization || user.organization || user.company || '',
      phone:        prev.phone        || user.phone        || loadPhone(userId),
    }));
    setBilling(prev => ({ ...prev, org_name: prev.org_name || user.organization || user.company || '' }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Sync contact org → billing org_name (for non-logged-in users)
  useEffect(() => {
    if (contact.organization && !billing.org_name) {
      setBilling(prev => ({ ...prev, org_name: contact.organization }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact.organization]);

  // Auto-submit after login if pendingSubmit
  useEffect(() => {
    if (!pendingSubmit || !user) return;
    handleSubmit();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Persist phone to localStorage whenever it changes
  useEffect(() => {
    if (contact.phone) savePhone(userId, contact.phone);
  }, [contact.phone, userId]);

  const setC = (k, v) => setContact(p => ({ ...p, [k]: v }));
  const setB = (k, v) => setBilling(p => ({ ...p, [k]: v }));
  const setS = (k, v) => setShipping(p => ({ ...p, [k]: v }));

  const handleSubmit = async e => {
    e?.preventDefault();
    if (items.length === 0) { setError('Add at least one product to your quote.'); return; }
    if (!billing.org_name?.trim()) { setError('Billing address organisation name is required.'); return; }
    if (!isAddrComplete(billing)) { setError('Please complete your billing address — street, city, state, postal code and country are all required.'); return; }
    if (!sameAsShipping && !shipping.org_name?.trim()) { setError('Shipping address organisation name is required.'); return; }
    if (!sameAsShipping && !isAddrComplete(shipping)) { setError('Please complete your shipping address — street, city, state, postal code and country are all required.'); return; }
    // Auth gate — prompt login if not signed in, then auto-submit after login
    if (!user) {
      setPendingSubmit(true);
      setError('');
      openLabAuth('Sign in to submit your quote request — your details are saved');
      return;
    }
    setPendingSubmit(false);
    // Use contact details from user profile
    const contactOrg   = user.organisation || user.organization || user.company || '';
    const contactPhone = user.phone || user.mobile || contact.phone || '';
    if (!contactOrg.trim() || !contactPhone.trim()) {
      setError('Your profile is incomplete. Please add your organisation name and phone number in Account Settings before submitting a quote.');
      return;
    }
    setSubmitting(true); setError('');
    // Save to localStorage (offline fallback) and to DB (cross-session persistence)
    saveAddress(userId, billing);
    if (!sameAsShipping && isAddrComplete(shipping)) saveAddress(userId, shipping);
    if (token) {
      saveAddressesToDB(
        token,
        billing,
        (!sameAsShipping && isAddrComplete(shipping)) ? shipping : null,
        contactPhone,
      );
    }

    const payload = {
      type:             'rfq',
      brand_name:       brand?.name || 'Multi-Brand',
      name:             user.name || '',
      email:            user.email || '',
      organization:     contactOrg,
      phone:            contactPhone,
      message:          notes,
      items:            items.map(p => ({
        id:             p.id,
        name:           p.name,
        brand:          p.brand || brand?.name || '',
        catalog_number: p.catalog_number || '',
        cas_number:     p.cas_number     || '',
        category:       p.category       || '',
        pack_size:      p.pack_size      || '',
        price:          p.price          || '',
        quantity:       qtys[p.id] || '1',
      })),
      billing_address:  billing,
      shipping_address: sameAsShipping ? billing : shipping,
      currency:         localStorage.getItem('biomall_currency') || 'INR',
    };

    try {
      const res  = await fetch(`${API}/api/brand-enquiry`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) { setDone(true); }
      else { setError(data.error || 'Submission failed. Please try again.'); }
    } catch { setError('Network error. Please try again.'); }
    finally   { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4" onClick={onClose}>
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center" onClick={e => e.stopPropagation()}>
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="text-base font-bold text-gray-900 mb-1">Quote request submitted!</div>
          <div className="text-sm text-gray-500 mb-4">We'll get back to you within 24 hours with pricing and availability.</div>
          <button onClick={() => { onSubmitDone(); onClose(); }}
            className="px-6 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-full hover:bg-blue-700">
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <div>
            <div className="text-base font-bold text-gray-900">Get Best Price</div>
            <div className="text-xs text-gray-500">{brand?.name ? `${brand.name} · ` : ''}{items.length} product{items.length !== 1 ? 's' : ''}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-xl">{error}</div>}

          {/* Product list */}
          <div>
            <div className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">Products</div>
            <div className="space-y-2">
              {items.map(p => (
                <div key={p.id} className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-xl p-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-gray-900 line-clamp-1">{p.name}</div>
                    {(p.brand && brand?.name && p.brand !== brand.name) && (
                      <div className="text-xs text-blue-600 font-semibold">{p.brand}</div>
                    )}
                    <div className="flex gap-2 text-xs text-blue-700 mt-0.5 flex-wrap">
                      {p.catalog_number && <span>Cat# {p.catalog_number}</span>}
                      {p.cas_number     && <span>CAS {p.cas_number}</span>}
                      {p.pack_size      && <span>{p.pack_size}</span>}
                      {p.price          && <span className="font-semibold">{p.price}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-gray-500">Qty:</span>
                      <input type="number" min="1"
                        value={qtys[p.id] || '1'}
                        onChange={e => setQtys(prev => ({ ...prev, [p.id]: e.target.value }))}
                        onFocus={e => e.target.select()}
                        className="w-12 text-xs border border-gray-200 rounded-lg px-1.5 py-1 outline-none text-center"
                      />
                    </div>
                    <button type="button" onClick={() => onRemoveItem(p.id)}
                      className="text-red-400 hover:text-red-600 text-sm font-bold leading-none">×</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Billing address */}
          <div>
            <SavedAddressPicker addresses={savedAddresses} onSelect={addr => setBilling(prev => ({ ...addr, org_name: addr.org_name || prev.org_name || contact.organization || '' }))} />
            <AddrForm val={billing} set={setB} title="Billing address" />
          </div>

          {/* Shipping toggle */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer mb-2">
              <input type="checkbox" checked={sameAsShipping} onChange={e => setSame(e.target.checked)}
                className="w-3.5 h-3.5 rounded border-gray-300" />
              <span className="text-xs text-gray-600">Shipping address same as billing</span>
            </label>
            {!sameAsShipping && (
              <div>
                <SavedAddressPicker addresses={savedAddresses} onSelect={addr => setShipping(prev => ({ ...addr, org_name: addr.org_name || prev.org_name || contact.organization || '' }))} />
                <AddrForm val={shipping} set={setS} title="Shipping address" showGst={false} />
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <div className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">Notes</div>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Any special requirements, delivery timeline, COA needed..."
              rows={2}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-xl outline-none resize-none focus:border-blue-400"
            />
          </div>

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 font-semibold flex items-center justify-center gap-1.5"
              data-testid="rfq-add-more-btn">
              + Add More Products
            </button>
            <button type="submit" disabled={submitting || items.length === 0}
              className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-bold rounded-xl transition-colors"
              data-testid="submit-rfq-btn">
              {submitting ? 'Submitting…' : `Get Best Price (${items.length} product${items.length !== 1 ? 's' : ''})`}
            </button>
          </div>
          <p className="text-xs text-gray-400 text-center mt-2 flex items-center justify-center gap-1" data-testid="rfq-fulfillment-disclaimer">
            <span>&#9432;</span> Your quote will be fulfilled by BioMall or routed directly to the manufacturer.
          </p>
        </form>
      </div>
    </div>
  );
}
